import React, { useState, useEffect, useCallback } from 'react';
import { CATEGORIES } from '../../mock';
import { CompanyIcon, fmtPrice, fmtVal, fmtLot } from '../CompanyCard';
import { toast } from 'sonner';
import { ClipboardCheck, ShieldCheck, PlusCircle, Building2, Clock, FileText, Check, X, Trash2 } from 'lucide-react';
import { adminSetKyc, adminAddMarket, adminKycSubmissions, adminDeleteUser } from '../../api';

export function KycReview({ token, users, onChanged }) {
  const [subs, setSubs] = useState([]);
  const roster = users.filter((u) => u.kyc !== 'pending');

  const loadSubs = useCallback(async () => {
    try { setSubs(await adminKycSubmissions(token)); } catch { /* noop */ }
  }, [token]);

  useEffect(() => { loadSubs(); }, [loadSubs, users]);

  const setStatus = async (id, status) => {
    try {
      await adminSetKyc(token, id, status);
      toast.success(status === 'verified' ? 'Investor accredited' : status === 'rejected' ? 'KYC rejected' : 'Status updated');
      onChanged && onChanged();
      loadSubs();
    } catch {
      toast.error('Update failed');
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Permanently delete "${u.name}"? This removes their account, holdings, deposits and KYC. This cannot be undone.`)) return;
    try {
      await adminDeleteUser(token, u.id);
      toast.success('User deleted', { description: `${u.name} has been permanently removed.` });
      onChanged && onChanged();
      loadSubs();
    } catch {
      toast.error('Delete failed');
    }
  };

  const accrLabel = {
    networth: 'Net Worth > $1M', income: 'Income > $200K',
    finra: 'FINRA License', institution: 'Institution $5M+',
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl bg-[var(--panel)] border border-[var(--border)] p-6">
        <div className="flex items-center gap-2">
          <ClipboardCheck size={16} className="text-[var(--orange)]" />
          <h3 className="font-display font-semibold text-[15px] text-white">PENDING KYC REVIEW ({subs.length})</h3>
        </div>

        {subs.length === 0 ? (
          <div className="mt-4 py-10 text-center rounded-lg border border-dashed border-[var(--border)] text-[var(--muted)]">
            <ShieldCheck size={26} className="mx-auto text-[var(--muted-2)]" />
            <p className="text-[13px] mt-3">No pending KYC reviews. All submissions cleared.</p>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {subs.map((s) => {
              const d = s.data || {};
              return (
                <div key={s.userId} className="rounded-lg bg-[var(--bg)] border border-[var(--border)] p-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[14px] text-white font-medium">{d.fullName || s.name}</span>
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-[#2a1a0e] text-[var(--orange)] border border-[#4a2f14]">PENDING</span>
                      </div>
                      <div className="text-[11px] font-mono text-[var(--muted-2)] mt-0.5">{s.email || s.userId.slice(0, 8)} · UID {s.userId.slice(0, 8)}…</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setStatus(s.userId, 'rejected')} className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#5a2020] text-[var(--red)] text-[12px] hover:bg-[#2a1010] transition-colors"><X size={13} /> Reject</button>
                      <button onClick={() => setStatus(s.userId, 'verified')} className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-[var(--green)] text-[#052e20] text-[12px] font-semibold hover:bg-[#13b878] transition-colors"><Check size={13} /> Approve</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-[12px]">
                    <Detail k="DOB" v={d.dob || '—'} />
                    <Detail k="COUNTRY" v={d.country || '—'} />
                    <Detail k="ID TYPE" v={d.idType || '—'} />
                    <Detail k="ID NUMBER" v={d.idNumber || '—'} />
                    <Detail k="ACCREDITATION" v={accrLabel[d.accreditation] || d.accreditation || '—'} />
                    <Detail k="NET WORTH" v={d.estimatedNetWorth ? `$${Number(d.estimatedNetWorth).toLocaleString()}` : '—'} />
                    <Detail k="SSN/TIN" v={d.ssnLast4 ? `••••${d.ssnLast4}` : '—'} />
                    <Detail k="PHONE" v={d.phone || '—'} />
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {d.idDocName && <Doc name={d.idDocName} label="ID Document" />}
                    {d.proofDocName && <Doc name={d.proofDocName} label="Accreditation Proof" />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="rounded-xl bg-[var(--panel)] border border-[var(--border)] p-6">
        <h4 className="text-[11px] font-mono tracking-[0.12em] text-[var(--muted-2)] mb-3">INVESTOR ROSTER</h4>
        <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
          {roster.map((u) => (
            <div key={u.id} className="flex items-center justify-between p-3 rounded-lg bg-[var(--bg)] border border-[var(--border-soft)]">
              <div>
                <div className="text-[13px] text-white">{u.name}</div>
                <div className="text-[10.5px] font-mono text-[var(--muted-2)]">{u.id.slice(0, 8)}…</div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-[9px] font-mono tracking-widest ${u.kyc === 'verified' ? 'text-[var(--green)]' : u.kyc === 'rejected' ? 'text-[var(--red)]' : 'text-[var(--muted-2)]'}`}>{u.kyc.toUpperCase()}</span>
                {u.kyc === 'verified' ? (
                  <button onClick={() => setStatus(u.id, 'unverified')} className="text-[11px] px-2.5 py-1.5 rounded-md border border-[#5a2020] text-[var(--red)] hover:bg-[#2a1010] transition-colors">Revoke</button>
                ) : (
                  <button onClick={() => setStatus(u.id, 'verified')} className="text-[11px] px-2.5 py-1.5 rounded-md bg-[var(--green)] text-[#052e20] font-semibold hover:bg-[#13b878] transition-colors">Approve</button>
                )}
                <button
                  data-testid={`admin-delete-user-${u.id}`}
                  onClick={() => removeUser(u)}
                  title="Delete user permanently"
                  className="flex items-center justify-center w-8 h-8 rounded-md border border-[#5a2020] text-[var(--red)] hover:bg-[#2a1010] transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Detail({ k, v }) {
  return (
    <div>
      <div className="text-[9px] font-mono tracking-widest text-[var(--muted-2)]">{k}</div>
      <div className="text-white mt-0.5 truncate">{v}</div>
    </div>
  );
}

function Doc({ name, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-md bg-[var(--panel-2)] border border-[var(--border)] text-[var(--muted)]">
      <FileText size={12} className="text-[var(--orange)]" /> {label}: <span className="text-white">{name}</span>
    </span>
  );
}

export function AddMarket({ token, onAdded }) {
  const [form, setForm] = useState({ name: '', ticker: '', category: CATEGORIES[1], indicativePrice: '', targetValuation: '', openDate: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.name || !form.ticker) {
      toast.error('Company name and ticker are required');
      return;
    }
    setBusy(true);
    try {
      await adminAddMarket(token, {
        name: form.name,
        ticker: form.ticker,
        category: form.category,
        indicativePrice: parseFloat(form.indicativePrice || '0') || 0,
        targetValuation: parseFloat(form.targetValuation || '0') || 0,
        openDate: form.openDate || 'TBD',
      });
      toast.success('Market added to pipeline', { description: `${form.ticker.toUpperCase()} queued for onboarding.` });
      setForm({ name: '', ticker: '', category: CATEGORIES[1], indicativePrice: '', targetValuation: '', openDate: '' });
      onAdded && onAdded();
    } catch {
      toast.error('Failed to add market');
    } finally {
      setBusy(false);
    }
  };

  const Field = ({ label, k, placeholder, mono }) => (
    <div>
      <label className="text-[10px] font-mono tracking-widest text-[var(--muted-2)]">{label}</label>
      <input value={form[k]} onChange={set(k)} placeholder={placeholder} className={`w-full mt-1.5 bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-[14px] text-white placeholder:text-[var(--muted-2)] focus:outline-none focus:border-[var(--orange)] transition-colors ${mono ? 'font-mono' : ''}`} />
    </div>
  );

  return (
    <div className="rounded-xl bg-[var(--panel)] border border-[var(--border)] p-6 max-w-2xl">
      <div className="flex items-center gap-2">
        <PlusCircle size={16} className="text-[var(--orange)]" />
        <h3 className="font-display font-semibold text-[15px] text-white">ADD NEW MARKET</h3>
      </div>
      <p className="text-[12.5px] text-[var(--muted)] mt-2">Onboard a new private company to the unlisted markets pipeline.</p>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="COMPANY NAME" k="name" placeholder="Acme Robotics Inc." />
        <Field label="TICKER" k="ticker" placeholder="ACME" mono />
        <div>
          <label className="text-[10px] font-mono tracking-widest text-[var(--muted-2)]">CATEGORY</label>
          <select value={form.category} onChange={set('category')} className="w-full mt-1.5 bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-[13px] text-white focus:outline-none focus:border-[var(--orange)] transition-colors">
            {CATEGORIES.filter((c) => c !== 'All').map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <Field label="OPEN DATE" k="openDate" placeholder="Q3 2026" />
        <Field label="INDICATIVE PRICE (USD)" k="indicativePrice" placeholder="75.00" mono />
        <Field label="TARGET VALUATION ($B)" k="targetValuation" placeholder="12.0" mono />
      </div>
      <button onClick={submit} disabled={busy} className="mt-6 flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[var(--orange)] text-white font-semibold text-[14px] hover:bg-[var(--orange-bright)] transition-colors disabled:opacity-60">
        <PlusCircle size={16} /> {busy ? 'ADDING…' : 'ADD TO PIPELINE'}
      </button>
    </div>
  );
}

export function Companies({ companies }) {
  return (
    <div className="rounded-xl bg-[var(--panel)] border border-[var(--border)] p-6">
      <div className="flex items-center gap-2 mb-4">
        <Building2 size={16} className="text-[var(--orange)]" />
        <h3 className="font-display font-semibold text-[15px] text-white">LISTED COMPANIES ({companies.length})</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="text-[10px] font-mono tracking-[0.12em] text-[var(--muted-2)] border-b border-[var(--border)]">
              <th className="py-3 px-3 font-normal">COMPANY</th>
              <th className="py-3 px-3 font-normal">CATEGORY</th>
              <th className="py-3 px-3 font-normal text-right">LAST</th>
              <th className="py-3 px-3 font-normal text-right">VALUATION</th>
              <th className="py-3 px-3 font-normal text-right">MIN LOT</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((c) => (
              <tr key={c.id} className="border-b border-[var(--border-soft)] hover:bg-[var(--panel-2)] transition-colors">
                <td className="py-3 px-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-[#1c1710] border border-[#3a2a18] flex items-center justify-center overflow-hidden">
                      <CompanyIcon name={c.icon} logo={c.logo} size={15} />
                    </div>
                    <div>
                      <div className="text-[13px] text-white">{c.name}</div>
                      <div className="text-[10px] font-mono text-[var(--orange)]">{c.ticker}</div>
                    </div>
                  </div>
                </td>
                <td className="py-3 px-3 text-[12px] text-[var(--muted)]">{c.category}</td>
                <td className="py-3 px-3 text-right font-mono text-[13px] text-[var(--orange)]">{fmtPrice(c.lastPrice)}</td>
                <td className="py-3 px-3 text-right font-mono text-[13px] text-[var(--salmon)]">{fmtVal(c.valuation)}</td>
                <td className="py-3 px-3 text-right font-mono text-[13px] text-[var(--text)]">{fmtLot(c.minLot)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
